import { u8aToHex, u8aConcat, u8aToU8a, hexToU8a } from '@polkadot/util';
import { blake2AsU8a, isEthereumAddress as isEthereumAddress$1, ethereumEncode, xxhashAsU8a } from '@polkadot/util-crypto';
import { twMerge } from 'tailwind-merge';
import { decodeAddress, encodeAddress } from '@polkadot/keyring';
import BigNumber from 'bignumber.js';

/**
 * Javascript's `Math` library for `BigInt`.
 * Taken from https://stackoverflow.com/questions/51867270/is-there-a-library-similar-to-math-that-supports-javascript-bigint/64953280#64953280
 */
const BigMath = {
  abs(x) {
    return x < 0n ? -x : x;
  },
  sign(x) {
    if (x === 0n) return 0n;
    return x < 0n ? -1n : 1n;
  },
  // TODO: Improve our babel/tsc config to let us use the `**` operator on bigint values.
  // Error thrown: Exponentiation cannot be performed on 'bigint' values unless the 'target' option is set to 'es2016' or later. ts(2791)
  // pow(base: bigint, exponent: bigint) {
  //   return base ** exponent
  // },
  min(value, ...values) {
    for (const v of values) if (v < value) value = v;
    return value;
  },
  max(value, ...values) {
    for (const v of values) if (v > value) value = v;
    return value;
  }
};

const bitLength$1 = 128;
function blake2Concat(input) {
  return u8aToHex(u8aConcat(blake2AsU8a(input, bitLength$1), u8aToU8a(input)));
}

const classNames = twMerge;

function decodeAnyAddress(encoded, ignoreChecksum, ss58Format) {
  try {
    return decodeAddress(encoded, ignoreChecksum, ss58Format);
  } catch (error) {
    if (typeof encoded !== "string") throw error;
    if (!isEthereumAddress$1(encoded)) throw error;
    return hexToU8a(encoded.slice("0x".length));
  }
}

/**
 * In TypeScript, a deferred promise refers to a pattern that involves creating a promise that can be
 * resolved or rejected at a later point in time, typically by code outside of the current function scope.
 *
 * This pattern is often used when dealing with asynchronous operations that involve multiple steps or when
 * the result of an operation cannot be immediately determined.
 */
function Deferred() {
  let resolve;
  let reject;
  let isPending = true;
  let isResolved = false;
  let isRejected = false;
  const promise = new Promise((innerResolve, innerReject) => {
    resolve = value => {
      isPending = false;
      isResolved = true;
      innerResolve(value);
    };
    reject = reason => {
      isPending = false;
      isRejected = true;
      innerReject(reason);
    };
  });
  return {
    promise,
    resolve,
    reject,
    isPending: () => isPending,
    isResolved: () => isResolved,
    isRejected: () => isRejected
  };
}

function encodeAnyAddress(key, ss58Format) {
  try {
    return encodeAddress(key, ss58Format);
  } catch (error) {
    if (typeof key !== "string") throw error;
    if (!isEthereumAddress$1(key)) throw error;
    return ethereumEncode(key);
  }
}

const MIN_DIGITS = 4; // less truncates more than what compact formating is
const MAX_DECIMALS_FORMAT = 12;

/**
 * Custom decimal number formatting for Talisman
 * note that the NumberFormat().format() call is the ressource heavy part, it's not worth trying to optimize other parts
 * @param num input number
 * @param digits number of significant digits to display
 * @param locale locale used to format the number
 * @param options formatting options
 * @returns the formatted value
 */
const formatDecimals = (num, digits = MIN_DIGITS, options = {}, locale = "en-US") => {
  if (num === null || num === undefined) return "";
  if (digits < MIN_DIGITS) digits = MIN_DIGITS;
  const value = new BigNumber(num);
  // very small numbers should display "< 0.0001"
  const minDisplayVal = 1 / Math.pow(10, digits);
  if (value.gt(0) && value.lt(minDisplayVal)) return `< ${formatDecimals(minDisplayVal)}`;

  // count digits
  const flooredValue = value.integerValue();
  const intDigits = flooredValue.isEqualTo(0) ? 0 : flooredValue.toString().length;

  // we never want to display a rounded up value
  // to prevent JS default rounding, we will remove/truncate insignificant digits ourselves before formatting
  let truncatedValue = value;
  //remove insignificant fraction digits
  const excessFractionDigitsPow10 = new BigNumber(10).pow(digits > intDigits ? digits - intDigits : 0);
  truncatedValue = truncatedValue.multipliedBy(excessFractionDigitsPow10).integerValue().dividedBy(excessFractionDigitsPow10);

  //remove insignificant integer digits
  const excessIntegerDigits = new BigNumber(intDigits > digits ? intDigits - digits : 0);
  const excessIntegerDigitsPow10 = new BigNumber(10).pow(excessIntegerDigits);
  if (excessIntegerDigits.gt(0)) truncatedValue = truncatedValue.dividedBy(excessIntegerDigitsPow10).integerValue().multipliedBy(excessIntegerDigitsPow10);

  // format

  return Intl.NumberFormat(locale, {
    //compact notation (K, M, B) if above 9999
    notation: truncatedValue.gt(9999) ? "compact" : "standard",
    maximumSignificantDigits: digits + (truncatedValue.lt(1) ? 1 : 0),
    ...options
  }).format(truncatedValue.toNumber());
};

function hasOwnProperty(obj, prop) {
  if (typeof obj !== "object") return false;
  if (obj === null) return false;
  return prop in obj;
}

function isArrayOf(array, func) {
  if (array.length > 0 && array[0] instanceof func) return true;
  return false;
}

const isBigInt = value => typeof value === "bigint";

const isEthereumAddress = address => !!address && address.startsWith("0x") && address.length === 42;

function planckToTokens(planck, tokenDecimals) {
  if (typeof planck !== "string" || typeof tokenDecimals !== "number") return;
  const base = 10;
  const exponent = -1 * tokenDecimals;
  const multiplier = base ** exponent;
  return new BigNumber(planck).multipliedBy(multiplier).toString(10);
}

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const throwAfter = (ms, reason) => new Promise((_, reject) => setTimeout(() => reject(new Error(reason)), ms));

function tokensToPlanck(tokens, tokenDecimals) {
  if (typeof tokens !== "string" || typeof tokenDecimals !== "number") return;
  const base = 10;
  const exponent = tokenDecimals;
  const multiplier = base ** exponent;
  return new BigNumber(tokens).multipliedBy(multiplier).toString(10);
}

const bitLength = 64;
function twox64Concat(input) {
  return u8aToHex(u8aConcat(xxhashAsU8a(input, bitLength), u8aToU8a(input)));
}

export { BigMath, Deferred, MAX_DECIMALS_FORMAT, blake2Concat, classNames, decodeAnyAddress, encodeAnyAddress, formatDecimals, hasOwnProperty, isArrayOf, isBigInt, isEthereumAddress, planckToTokens, sleep, throwAfter, tokensToPlanck, twox64Concat };
